import type {
  McpRuntime,
  ServerEntry,
  ServerEntryInput,
  ServerTransport,
} from '@littlebird/axon-mcp';

/**
 * One env/header row in the editor. Deliberately carries no `value` — the
 * real value lives in `LocalServerDraft.rowValues`, keyed by `id`, so a
 * plain `JSON.stringify(draft.maskedRows)` (logs, error text, snapshots)
 * can never surface a secret.
 */
export interface LocalServerDraftRow {
  id: string;
  key: string;
  reveal: boolean;
}

export interface LocalServerDraft {
  name: string;
  description: string;
  enabled: boolean | undefined;
  transportKind: ServerTransport['kind'];
  runtime?: McpRuntime;
  packageSpec?: string;
  command?: string;
  url?: string;
  serverId?: string;
  args: string[];
  maskedRows: LocalServerDraftRow[];
  rowValues: Record<string, string>;
}

function keyValueRowsFrom(record: Record<string, string> | undefined): {
  rows: LocalServerDraftRow[];
  rowValues: Record<string, string>;
} {
  const rows: LocalServerDraftRow[] = [];
  const rowValues: Record<string, string> = {};
  for (const [key, value] of Object.entries(record ?? {})) {
    const id = crypto.randomUUID();
    rows.push({ id, key, reveal: false });
    rowValues[id] = value;
  }
  return { rows, rowValues };
}

export function createLocalServerDraft(entry: ServerEntry): LocalServerDraft {
  const { transport } = entry;
  const record = transport.kind === 'http' ? transport.headers : transport.env;
  const { rows, rowValues } = keyValueRowsFrom(record);

  return {
    name: entry.name,
    description: entry.description ?? '',
    enabled: entry.enabled,
    transportKind: transport.kind,
    runtime: transport.kind === 'stdio' ? transport.runtime : undefined,
    packageSpec: transport.kind === 'stdio' ? transport.packageSpec : undefined,
    command: transport.kind === 'binary' ? transport.command : undefined,
    url: transport.kind === 'http' ? transport.url : undefined,
    serverId: transport.kind === 'bundled' ? transport.serverId : undefined,
    args: transport.kind === 'http' ? [] : [...(transport.args ?? [])],
    maskedRows: rows,
    rowValues,
  };
}

export function addDraftRow(draft: LocalServerDraft): LocalServerDraft {
  const id = crypto.randomUUID();
  return {
    ...draft,
    maskedRows: [...draft.maskedRows, { id, key: '', reveal: false }],
    rowValues: { ...draft.rowValues, [id]: '' },
  };
}

export function removeDraftRow(
  draft: LocalServerDraft,
  id: string
): LocalServerDraft {
  return {
    ...draft,
    maskedRows: draft.maskedRows.filter((row) => row.id !== id),
    rowValues: Object.fromEntries(
      Object.entries(draft.rowValues).filter(([rowId]) => rowId !== id)
    ),
  };
}

export function toggleDraftRowReveal(
  draft: LocalServerDraft,
  id: string
): LocalServerDraft {
  return {
    ...draft,
    maskedRows: draft.maskedRows.map((row) =>
      row.id === id ? { ...row, reveal: !row.reveal } : row
    ),
  };
}

export function updateDraftRowField(
  draft: LocalServerDraft,
  id: string,
  field: 'key' | 'value',
  value: string
): LocalServerDraft {
  if (field === 'value') {
    return { ...draft, rowValues: { ...draft.rowValues, [id]: value } };
  }
  return {
    ...draft,
    maskedRows: draft.maskedRows.map((row) =>
      row.id === id ? { ...row, key: value } : row
    ),
  };
}

function recordFromRows(
  draft: LocalServerDraft
): Record<string, string> | undefined {
  const entries = draft.maskedRows
    .map((row) => [row.key.trim(), draft.rowValues[row.id] ?? ''] as const)
    .filter(([key]) => key !== '');
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function buildTransport(draft: LocalServerDraft): ServerTransport {
  const args = draft.args.map((argument) => argument.trim()).filter(Boolean);
  const record = recordFromRows(draft);

  switch (draft.transportKind) {
    case 'stdio':
      return {
        kind: 'stdio',
        runtime: draft.runtime ?? 'node',
        packageSpec: (draft.packageSpec ?? '').trim(),
        ...(args.length > 0 && { args }),
        ...(record && { env: record }),
      };
    case 'binary':
      return {
        kind: 'binary',
        command: (draft.command ?? '').trim(),
        ...(args.length > 0 && { args }),
        ...(record && { env: record }),
      };
    case 'http':
      return {
        kind: 'http',
        url: (draft.url ?? '').trim(),
        ...(record && { headers: record }),
      };
    case 'bundled':
      return {
        kind: 'bundled',
        serverId: draft.serverId ?? '',
        ...(args.length > 0 && { args }),
        ...(record && { env: record }),
      };
  }
}

export function buildServerEntryChanges(
  original: ServerEntry,
  draft: LocalServerDraft
): Partial<ServerEntryInput> & { enabled?: boolean } {
  return {
    name: draft.name,
    // A blank draft only needs to reach the server when it's actually
    // clearing something — `updateServerEntry` skips `undefined` fields, so
    // '' persists a clear, while `undefined` leaves an already-blank
    // description alone instead of writing a needless empty string.
    description: draft.description || (original.description ? '' : undefined),
    transport: buildTransport(draft),
    setup: original.setup,
    enabled: draft.enabled,
  };
}
